import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  Search,
  Phone,
  LifeBuoy,
  Play,
  Bot,
  Zap,
  Clock,
  History,
  Shield,
  Ticket,
  Users,
  ShieldCheck,
  Lock,
  BarChart2,
} from 'lucide-react';
import { SectionHeading, FeatureCard, CTAButton, FeatureCheck, StatCard, CTACard, UnifiedFooter } from '../components';
import { Nav } from '../components/Nav';
import WaveformVisualizer from '../components/WaveformVisualizer';
import patternSvg from '../assets/pattern.svg';
import vantaLogo from '../assets/vanta.png';
import dropboxLogo from '../assets/dropbox.svg';
import firstroundLogo from '../assets/firstround.png';
import '../styles/global.css';
import FirewallLayerStack from '../components/FirewallLayerStack';

const HelpdeskPage: React.FC = () => {
  const [hoveredLayer, setHoveredLayer] = useState<number | null>(null);
  const solutionSectionRef = useRef<HTMLElement>(null);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  // Prevent flash of unstyled content
  useEffect(() => {
    document.body.style.backgroundColor = '#ffffff';
    document.body.style.color = '#000000';
    document.body.style.margin = '0';

    // Set SEO title and meta
    document.title = 'Syntra | Detect social engineering, verify requests';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Syntra helps IT helpdesks protect against social engineering and deepfakes with detection and verification tools..');
    }

    return () => {
      document.body.style.backgroundColor = '';
      document.body.style.color = '';
      document.body.style.margin = '';
    };
  }, []);

  return (
    <motion.div
      className="min-h-screen bg-white text-dark-900 font-sans overflow-x-clip"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      variants={containerVariants}
    >
      {/* Navigation */}
      <Nav activeLink="trust" />
      {/* Hero Section */}
      <motion.section className="hero_section pt-36 sm:pt-[180px] pb-24 bg-blue-700 text-white" variants={itemVariants}>
        <div className="page-padding">
          <div className="container-medium">
            <div className="grid grid-cols-1 md:grid-cols-7 gap-12 items-start">
              <div className="hero_content md:col-span-5">
                <h1 className="heading-style-h1 text-4xl sm:text-5xl lg:text-6xl font-normal mb-8 text-white leading-tight">
                  Detect impostors.<br />Verify people.
                </h1>
                <p className="text-lg sm:text-xl mb-10 text-blue-100 leading-relaxed">
                  Monitor for social engineering attacks on your helpdesk. Block vishing during password and MFA resets. And step up to deepfake-resistant identity verification when your agents need it.
                </p>
                <div className="hero_button-group flex flex-col sm:flex-row gap-4 mb-10">
                  <CTAButton href="https://zcal.co/t/syntra/assessment" variant="white">
                    Free assessment
                  </CTAButton>
                  <CTAButton href="https://zcal.co/t/syntra/demo" variant="secondary">
                    Request demo
                  </CTAButton>
                </div>
                <p className="text-sm text-blue-200 mb-4">
                  Syntra helps detect and protect against
                </p>
                <div className="hero_features grid grid-cols-2 gap-4 mb-8">
                  <FeatureCheck text="Account takeover calls" variant="light" />
                  <FeatureCheck text="Vishing attempts" variant="light" />
                  <FeatureCheck text="MFA reset scams" variant="light" />
                  <FeatureCheck text="... and more" variant="light" />
                </div>
                <div className="hero_attribution text-dark-500 text-sm">
                  <p className="text-blue-200">Built by founding engineers from</p>
                  <div className="flex gap-6 mt-2 items-center">
                    <img src={vantaLogo} alt="Vanta logo" className="h-8 w-24 object-contain filter grayscale brightness-0 invert opacity-90" />
                    <img src={dropboxLogo} alt="Dropbox logo" className="h-8 w-24 object-contain filter grayscale brightness-0 invert opacity-90" />
                    <img src={firstroundLogo} alt="First Round Capital logo" className="h-8 w-24 object-contain filter grayscale brightness-0 invert opacity-90" />
                  </div>
                </div>
              </div>
              <div className="md:col-span-2 flex items-start justify-center pt-8">
                <WaveformVisualizer size={400} />
              </div>
            </div>
          </div>
        </div>
      </motion.section>
      {/* Assessment Banner */}
      <motion.section id="assessment" className="assessment_section py-10 sm:py-14 bg-white" variants={itemVariants}>
        <div className="page-padding">
          <div className="container-medium">
            <div className="assessment_wrapper p-10 sm:p-12 rounded-2xl bg-blue-700 border border-blue-600 shadow-sm">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-8">
                <div className="assessment_content">
                  <h3 className="text-base font-mono text-blue-200 uppercase tracking-wider mb-3">COMPLIMENTARY ASSESSMENT</h3>
                  <p className="text-blue-100 text-lg leading-relaxed mb-6">
                    Concerned about your IT security controls? We'll audit calls or red-team your helpdesk workflows, looking for policy gaps.
                  </p>
                  <CTAButton href="#contact" variant="white">
                    Get started
                  </CTAButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.section>
      <motion.section className="problem_section py-20 sm:py-28 bg-white relative overflow-hidden" variants={itemVariants}>
        {/* Image background that bleeds to right edge */}
        <div className="absolute top-0 bottom-0 right-0 w-2/5 hidden md:block z-0">
          <div className="h-full relative overflow-hidden">
            <div className="pattern-wrapper absolute inset-0">
              <div className="pattern-container absolute inset-0 flex items-center justify-center mt-48">
                <img
                  src={patternSvg}
                  alt="Security Pattern"
                  className="w-[150%] h-[150%] object-cover"
                  style={{
                    maxWidth: 'none',
                    transform: 'rotate(15deg) scale(0.65)',
                    transformOrigin: 'center center'
                  }}
                />
                {/* Security Threat Indicators */}
                <div className="absolute z-10 w-full h-full">
                  {/* Account Takeover Indicator */}
                  <div className="absolute top-[25%] left-[20%]">
                    <div className="relative">
                      <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                      <div className="absolute top-0 left-0 w-3 h-3 bg-red-500 rounded-full animate-ping opacity-75"></div>
                      <div className="security-threat-label bg-black text-white text-xs px-3 py-1 rounded absolute -top-10 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                        Account takeover attempt
                      </div>
                    </div>
                  </div>
                  {/* Vishing Indicator */}
                  <div className="absolute top-[50%] right-[30%]">
                    <div className="relative">
                      <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                      <div className="absolute top-0 left-0 w-3 h-3 bg-red-500 rounded-full animate-ping opacity-75"></div>
                      <div className="security-threat-label bg-black text-white text-xs px-3 py-1 rounded absolute -top-10 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                        Executive impersonation
                      </div>
                    </div>
                  </div>
                  {/* Adversarial Audio Indicator */}
                  <div className="absolute bottom-[25%] left-[25%]">
                    <div className="relative">
                      <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                      <div className="absolute top-0 left-0 w-3 h-3 bg-red-500 rounded-full animate-ping opacity-75"></div>
                      <div className="security-threat-label bg-black text-white text-xs px-3 py-1 rounded absolute -top-10 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                        Fake new hire
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Text content with proper margins - positioned above background */}
        <div className="relative z-10 page-padding">
          <div className="container-medium">
            <div className="max-w-2xl bg-white pr-12">
              <SectionHeading
                caption="THE PROBLEM"
                title="Social engineering via phone is the fastest growing breach vector"
                description={
                  <>
                    <p className="mb-4">
                      The IT help desk is the castle gate. Attackers like Scattered Spider know this.
                    </p>
                    <p className="mb-4">
                      They use fake calls and doctored voices to trick companies into resetting passwords of trusted team members.
                    </p>
                    <p className="mb-4">
                      Helpdesks use knowledge based questions and call back verification to defend - inconsistent, expensive, and circumventable processes.
                    </p>
                    <p>
                      Just ask <a href="https://westoahu.hawaii.edu/cyber/global-weekly-exec-summary/alphv-hackers-reveal-details-of-mgm-cyber-attack/" target="_blank" rel="noopener noreferrer" className="underline">MGM</a>, <a href="https://www.axios.com/2025/06/20/aflac-insurance-data-breach-cybersecurity" target="_blank" rel="noopener noreferrer" className="underline">Aflac</a>, and <a href="https://www.the-independent.com/news/business/m-s-coop-hack-scattered-spider-it-worker-b2745218.html" target="_blank" rel="noopener noreferrer" className="underline">Marks & Spencer</a>.
                    </p>
                  </>
                }
                textColor="dark"
              />
              <div className="stats_grid grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 mt-10">
                <StatCard value="760%" label="Social engineering growth YoY" variant="light" />
                <StatCard value="3 in 5" label="CISOs report helpdesk attempts" variant="light" />
                <StatCard value="$9.4M" label="Average breach cost" variant="light" />
              </div>
              <div className="flex items-center mt-4">
                <span className="text-xs text-gray-400 flex items-center">
                  <svg
                    className="inline-block mr-1"
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <circle cx="6" cy="6" r="6" fill="#9CA3AF" />
                    <text x="6" y="9" textAnchor="middle" fontSize="8" fill="white" fontFamily="sans-serif" fontWeight="bold">i</text>
                  </svg>
                  <span>Gartner 2024</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </motion.section>
      {/* Solution Section */}
      <motion.section
        ref={solutionSectionRef}
        className="solution_section py-24 sm:py-28 bg-blue-700 text-white"
        variants={itemVariants}
      >
        <div className="page-padding">
          <div className="container-medium">
            <SectionHeading
              caption="THE SOLUTION"
              title="Detection and verification built into every IT ticket"
              description="Syntra continuously analyzes voice and behavioral context to identify call-based threats. If needed, it triggers deepfake-resistant verification."
              textColor="light"
            />
            <div className="mt-20 mb-8">
              <FirewallLayerStack activeLayer={hoveredLayer} setActiveLayer={setHoveredLayer} />
            </div>
          </div>
        </div>

      </motion.section>

      {/* WHY MFA FALLS SHORT */}
      <section className="why-mfa-falls-short-section py-20 sm:py-28 bg-gray-50">
        <div className="page-padding">
          <div className="container-medium">
            <SectionHeading
              caption="WHY USE SYNTRA"
              title="MFA and identity verification isn't enough"
              textColor="dark"
            />
            <div className="space-y-6 mt-8 text-gray-700 text-lg max-w-2xl mx-auto">
              <div className="flex items-start gap-3">
                <span className="mt-1 text-blue-700">
                  <svg width="20" height="20" fill="none" viewBox="0 0 20 20"><circle cx="10" cy="10" r="10" fill="#1D4ED8" /><text x="10" y="15" textAnchor="middle" fontSize="13" fill="white" fontFamily="sans-serif" fontWeight="bold">i</text></svg>
                </span>
                <p>
                  <strong>MFA resets are inevitable.</strong> Lost phones and forgotten passwords push employees back to the help desk calls, where attackers strike.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <span className="mt-1 text-blue-700">
                  <svg width="20" height="20" fill="none" viewBox="0 0 20 20"><circle cx="10" cy="10" r="10" fill="#1D4ED8" /><text x="10" y="15" textAnchor="middle" fontSize="13" fill="white" fontFamily="sans-serif" fontWeight="bold">i</text></svg>
                </span>
                <p>
                  <strong>IDV alone is clunky.</strong> Pure identity verification software creates unneeded friction on every ticket - or doesn't detect the security threat. Syntra monitors, then verifies only if needed.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <span className="mt-1 text-blue-700">
                  <svg width="20" height="20" fill="none" viewBox="0 0 20 20"><circle cx="10" cy="10" r="10" fill="#1D4ED8" /><text x="10" y="15" textAnchor="middle" fontSize="13" fill="white" fontFamily="sans-serif" fontWeight="bold">i</text></svg>
                </span>
                <p>
                  <strong>Identity starts on day one.</strong> MFA depends on the original identity being who they say they are. If you don't validate a new hire during onboarding or account reset, no downstream control will work.
                </p>
              </div>
              <div>
                <p>
                  Syntra closes the loop with a centralized, deepfake-resistant check.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <motion.section className="py-12 bg-blue-700 text-white">
        <div className="page-padding">
          <div className="container-medium text-center max-w-3xl mx-auto">
            <p className="text-xl italic">
              “Syntra's solution would cut almost a minute off every reset call and give my team
              the confidence to say ‘no’ to imposters.”
            </p>
            <p className="mt-3 text-sm text-blue-200">
              – Director of IT, Series C SaaS company
            </p>
          </div>
        </div>
      </motion.section>


      <motion.section className="integration_section py-20 sm:py-28 bg-white" variants={itemVariants}>
        <div className="page-padding">
          <div className="container-medium">
            <SectionHeading
              caption="INTEGRATION"
              title="Works with your existing stack"
              description="Connect Syntra to your identity, IT, or call center systems for seamless, secure verification."
              textColor="dark"
            />
            {(() => {
              // Import Lucide icons at the top of your file:
              // import { Shield, Ticket, Users } from 'lucide-react';
              // Stepper data for integration types
              const integrationTypes = [
                {
                  title: 'IT Service Management',
                  description: 'Plug into IT Service Management platforms to integrate into IT flows.',
                  icon: (
                    <Ticket className="text-blue-700 mr-2" size={28} aria-label="IT Service Management" />
                  ),
                  integrations: ['ServiceNow', 'Jira', 'Salesforce Service Cloud', 'Freshservice'],
                },
                {
                  title: 'Call Center Platforms',
                  description: 'Connect to call center platforms for real-time, non-intrusive detection.',
                  icon: (
                    <Users className="text-blue-700 mr-2" size={28} aria-label="HR Systems" />
                  ),
                  integrations: ['Amazon Connect', 'Genesys', 'NiCE CXOne'],
                },
                {
                  title: 'Identity Providers',
                  description: 'Seamlessly integrate with Okta, Azure AD, Ping Identity, and other SSO providers for source of truth.',
                  icon: (
                    <Shield className="text-blue-700 mr-2" size={28} aria-label="Identity Providers" />
                  ),
                  integrations: ['Okta', 'Azure AD', 'Ping Identity', 'OneLogin'],
                },
              ];
              const [selected, setSelected] = React.useState(0);
              const fadeKey = integrationTypes[selected].title;
              // Selector button styles
              const selectorBase = 'text-left px-5 py-2 rounded-lg border font-medium transition-all duration-200 w-full flex items-center gap-2';
              const selectorActive = 'bg-blue-700 text-white shadow border-blue-700';
              const selectorInactive = 'bg-white text-blue-700 border-blue-100 hover:bg-blue-50';
              return (
                <div className="flex flex-col md:flex-row gap-8 mt-12 items-stretch">
                  {/* Stepper Selector */}
                  <div className="flex flex-col gap-1 md:w-1/3 relative h-full justify-center">
                    {integrationTypes.map((type, idx) => (
                      <button
                        key={type.title}
                        onClick={() => { setSelected(idx); }}
                        className={`${selectorBase} text-base md:text-lg py-4 ${selected === idx ? selectorActive : selectorInactive}`}
                        style={{ minWidth: 0 }}
                        aria-current={selected === idx ? "step" : undefined}
                      >
                        {type.icon}
                        <span>{type.title}</span>
                      </button>
                    ))}
                  </div>
                  {/* Detail Card with fade animation */}
                  <div className="flex-1 flex flex-col items-center h-full">
                    <motion.div
                      key={fadeKey}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -16 }}
                      transition={{ duration: 0.4, ease: 'easeOut' }}
                      className="w-full"
                    >
                      <div className="bg-white rounded-2xl shadow p-8 flex flex-col items-start w-full border border-blue-100">
                        <div className="flex items-center mb-4">
                          <span className="text-3xl mr-3">{integrationTypes[selected].icon}</span>
                          <h3 className="text-2xl font-semibold text-blue-700">{integrationTypes[selected].title}</h3>
                        </div>
                        <p className="text-lg text-slate-700 mb-6">{integrationTypes[selected].description}</p>
                        <div className="flex flex-wrap gap-3">
                          {integrationTypes[selected].integrations.map((name) => (
                            <span
                              key={name}
                              className="inline-block bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm font-medium border border-blue-100"
                            >
                              {name}
                            </span>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </motion.section>

      {/* Value Props */}
      <motion.section
        className="value_props_section py-20 sm:py-28 bg-blue-700 text-white"
        variants={itemVariants}
      >
        <div className="page-padding">
          <div className="container-medium">
            <SectionHeading
              caption="WHY SYNTRA"
              title="Defend against social engineering - while reducing friction"
              description=""
              textColor="light"
            />

            <div className="value_props_grid grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-12">
              <FeatureCard
                title="Reduce risk"
                description="Detect social engineering over the phone - stopping fake hires and voice imposters before they reach an account."
                iconComponent={Lock}
                variant="dark"
              />
              <FeatureCard
                title="Standardize processes"
                description="Create one secure, auditable flow so that onboarding, MFA resets, and privilege changes follow the same enforced path."
                iconComponent={ShieldCheck}
                variant="dark"
              />
              <FeatureCard
                title="Save time"
                description="Use automated challenges only when needed - shaving 60 seconds from every ticket and erase ad-hoc verification calls."
                iconComponent={Clock}
                variant="dark"
              />
            </div>
          </div>
        </div>
      </motion.section>


      {/* CTA Section */}
      <motion.section id="contact" className="cta_section py-20 sm:py-28 bg-white" variants={itemVariants}>
        <div className="page-padding">
          <div className="container-medium">
            <SectionHeading
              caption="GET STARTED"
              title="Ready to defend the helpdesk?"
              description=""
              textColor="dark"
              className="mb-12"
            />
            <div className="cta_cards grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              <CTACard
                title="Complimentary assessment"
                description="We'll red-team and audit your processes to identify gaps and ITSM risk."
                buttonText="Schedule"
                buttonLink="https://zcal.co/t/syntra/assessment"
                iconComponent={Search}
              />
              <CTACard
                title="Product demo"
                description="We'll walk you through Syntra's product, architecture, and integrations."
                buttonText="Book demo"
                buttonLink="https://zcal.co/t/syntra/demo"
                iconComponent={Play}
              />
            </div>
          </div>
        </div>
      </motion.section>
      {/* Footer */}
      <UnifiedFooter itemVariants={itemVariants} />
    </motion.div>
  );
};

export default HelpdeskPage; 